import React, { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../supabase'

// ─── NOTE SQL ─────────────────────────────────────────────────────────────────
// Executer dans Supabase SQL Editor :
//
// CREATE TABLE IF NOT EXISTS messages_chantier (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   chantier_id uuid REFERENCES chantiers(id) ON DELETE CASCADE,
//   auteur_id uuid REFERENCES profiles(id),
//   auteur_nom text,
//   contenu text NOT NULL,
//   canal text CHECK (canal IN ('interne', 'client')),
//   created_at timestamptz DEFAULT now()
// );
// ALTER TABLE messages_chantier ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "messages_all" ON messages_chantier FOR ALL USING (true) WITH CHECK (true);
// ALTER PUBLICATION supabase_realtime ADD TABLE messages_chantier;
// ─────────────────────────────────────────────────────────────────────────────

export default function ChantierChat({ chantierId, canal, currentUserId, currentUserNom, readOnly = false }) {
  const [messages, setMessages] = useState([])
  const [texte, setTexte] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const bottomRef = useRef(null)

  const loadMessages = useCallback(async () => {
    const { data } = await supabase
      .from('messages_chantier')
      .select('*')
      .eq('chantier_id', chantierId)
      .eq('canal', canal)
      .order('created_at', { ascending: true })
    setMessages(data || [])
    setLoading(false)
  }, [chantierId, canal])

  useEffect(() => {
    setLoading(true)
    loadMessages()
    const channel = supabase.channel('chat-' + chantierId + '-' + canal)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages_chantier',
        filter: 'chantier_id=eq.' + chantierId,
      }, (payload) => {
        if (payload.new.canal === canal) {
          setMessages(prev => [...prev, payload.new])
        }
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [chantierId, canal, loadMessages])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const envoyer = async (e) => {
    e.preventDefault()
    if (!texte.trim() || sending) return
    setSending(true)
    const { error } = await supabase.from('messages_chantier').insert([{
      chantier_id: chantierId,
      auteur_id: currentUserId || null,
      auteur_nom: currentUserNom || 'Anonyme',
      contenu: texte.trim(),
      canal,
    }])
    if (!error) {
      setTexte('')
    }
    setSending(false)
  }

  const formatHeure = (ts) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

  const formatDateLabel = (ts) => {
    const d = new Date(ts)
    const now = new Date()
    const diff = Math.floor((now - d) / 86400000)
    if (diff === 0) return "Aujourd'hui"
    if (diff === 1) return 'Hier'
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
  }

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#a0aec0', fontSize: 14 }}>Chargement des messages...</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Zone messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <div style={{ textAlign: 'center', color: '#a0aec0' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>{'💬'}</div>
              <div style={{ fontSize: 13 }}>Aucun message. Demarrez la conversation !</div>
            </div>
          </div>
        ) : messages.map((msg, i) => {
          const isMine = currentUserId ? msg.auteur_id === currentUserId : msg.auteur_nom === currentUserNom
          const prev = messages[i - 1]
          const showDate = !prev || new Date(msg.created_at).toDateString() !== new Date(prev.created_at).toDateString()
          const sameAuthor = prev && prev.auteur_id === msg.auteur_id && !showDate
          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <div style={{ textAlign: 'center', margin: '12px 0 6px' }}>
                  <span style={{ fontSize: 11, color: '#a0aec0', background: '#f5f6fa', padding: '3px 12px', borderRadius: 12 }}>
                    {formatDateLabel(msg.created_at)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', marginBottom: 2 }}>
                {!isMine && !sameAuthor && (
                  <span style={{ fontSize: 11, color: '#718096', marginBottom: 3, marginLeft: 6 }}>{msg.auteur_nom}</span>
                )}
                <div style={{
                  maxWidth: '68%', padding: '9px 13px',
                  borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: isMine ? '#1D9E75' : 'white',
                  color: isMine ? 'white' : '#2d3748',
                  fontSize: 14, lineHeight: 1.45,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                }}>
                  <div>{msg.contenu}</div>
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2, textAlign: 'right' }}>
                    {formatHeure(msg.created_at)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )
        })}
        <div ref={bottomRef} />
      </div>

      {/* Saisie */}
      {!readOnly && (
        <form onSubmit={envoyer} style={{
          borderTop: '1px solid #e2e8f0', padding: '12px 16px',
          display: 'flex', gap: 8, background: '#fafafa', flexShrink: 0,
        }}>
          <input
            value={texte}
            onChange={e => setTexte(e.target.value)}
            placeholder="Ecrivez votre message..."
            style={{
              flex: 1, padding: '10px 16px', border: '1.5px solid #e2e8f0',
              borderRadius: 24, fontSize: 14, outline: 'none', background: 'white',
            }}
            onFocus={e => e.target.style.borderColor = '#1D9E75'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
          <button type="submit" disabled={!texte.trim() || sending} style={{
            background: texte.trim() ? '#1D9E75' : '#e2e8f0',
            color: texte.trim() ? 'white' : '#a0aec0',
            border: 'none', borderRadius: 24, padding: '10px 22px',
            fontSize: 14, fontWeight: 600, cursor: texte.trim() ? 'pointer' : 'default',
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}>
            Envoyer
          </button>
        </form>
      )}
    </div>
  )
}
